import {
  KeyboardEvent,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  ButtonSize,
  IconButton,
  PrimaryButton,
  SecondaryButton,
  UnstyledButton,
} from '@ardoq/button';
import {
  colors,
  easingTokens,
  space,
  transitionTokens,
} from '@ardoq/design-tokens';
import { TextArea } from '@ardoq/forms';
import { IconName } from '@ardoq/icons';
import { Box, FlexBox } from '@ardoq/layout';
import {
  AnimatedModalDialog,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalSize,
} from '@ardoq/modal';
import { Chip } from '@ardoq/status-ui';
import { caption, Text } from '@ardoq/typography';
import styled, { keyframes } from 'styled-components';
import { t, type I18nLocale, DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';

export enum AiFeedbackNegativeCategory {
  OTHER = 'OTHER',
  INACCURATE = 'INACCURATE',
  INCOMPLETE = 'INCOMPLETE',
  ERROR = 'ERROR',
}

const BAR_HEIGHT = '34px';

type AiFeedbackMode = 'default' | 'thankYou' | 'askingWhy';

export type AiFeedbackCategory<T extends string = AiFeedbackNegativeCategory> =
  {
    id: T;
    label: string;
  };

export type AiNegativeFeedback<T extends string = AiFeedbackNegativeCategory> =
  {
    category: T;
    extraInformation: string;
  };

type AiFeedbackLabels = {
  question: string;
  answerPositive: string;
  answerNegative: string;
  thankYou: string;
  tellUsWhy: string;
  hideFeedback: string;
  modalTitle: string;
  modalDescription: string;
  modalCategoriesLabel: string;
  modalInputPlaceholder: string;
  modalCancelButton: string;
  modalSubmitButton: string;
};

export type AiFeedbackLabelOverrides = Partial<AiFeedbackLabels>;

const buildDefaultCategories = (
  i18nLocale: I18nLocale
): AiFeedbackCategory[] => [
  {
    id: AiFeedbackNegativeCategory.INACCURATE,
    label: t(i18nLocale, 'ai:aiFeedback.category.inaccurate', 'Inaccurate'),
  },
  {
    id: AiFeedbackNegativeCategory.INCOMPLETE,
    label: t(i18nLocale, 'ai:aiFeedback.category.incomplete', 'Incomplete'),
  },
  {
    id: AiFeedbackNegativeCategory.ERROR,
    label: t(i18nLocale, 'ai:aiFeedback.category.error', 'Error'),
  },
  {
    id: AiFeedbackNegativeCategory.OTHER,
    label: t(i18nLocale, 'ai:aiFeedback.category.other', 'Other'),
  },
];

const buildDefaultLabels = (i18nLocale: I18nLocale): AiFeedbackLabels => ({
  question: t(i18nLocale, 'ai:aiFeedback.question', 'Was this helpful?'),
  answerPositive: t(
    i18nLocale,
    'ai:aiFeedback.answerPositive',
    'Yes, this helped me.'
  ),
  answerNegative: t(
    i18nLocale,
    'ai:aiFeedback.answerNegative',
    'No, this didn’t help.'
  ),
  thankYou: t(
    i18nLocale,
    'ai:aiFeedback.thankYouMessage',
    'Thanks for the feedback.'
  ),
  tellUsWhy: t(i18nLocale, 'ai:aiFeedback.tellUsWhyLabel', 'Tell us why'),
  hideFeedback: t(
    i18nLocale,
    'ai:aiFeedback.hideFeedbackLabel',
    'Hide Feedback'
  ),
  modalTitle: t(i18nLocale, 'ai:aiFeedback.modal.title', 'Help us improve'),
  modalDescription: t(
    i18nLocale,
    'ai:aiFeedback.modal.description',
    'Your feedback helps us refine what the AI generates.'
  ),
  modalCategoriesLabel: t(
    i18nLocale,
    'ai:aiFeedback.modal.categoriesLabel',
    'What went wrong? (required)'
  ),
  modalInputPlaceholder: t(
    i18nLocale,
    'ai:aiFeedback.modal.inputPlaceholder',
    'What was incorrect or missing? (optional)'
  ),
  modalCancelButton: t(
    i18nLocale,
    'ai:aiFeedback.modal.cancelButton',
    'Cancel'
  ),
  modalSubmitButton: t(
    i18nLocale,
    'ai:aiFeedback.modal.submitButton',
    'Submit feedback'
  ),
});

const resolveLabels = (
  i18nLocale: I18nLocale,
  overrides: AiFeedbackLabelOverrides | undefined
): AiFeedbackLabels => ({ ...buildDefaultLabels(i18nLocale), ...overrides });

type AiFeedbackFormState<T extends string> = {
  mode: AiFeedbackMode;
  modalIsOpen: boolean;
  selectedCategory: T | null;
  extraInformation: string;
};

const INITIAL_FORM_STATE = {
  mode: 'default',
  modalIsOpen: false,
  selectedCategory: null,
  extraInformation: '',
} as const;

const CATEGORY_KEY_OFFSETS: Record<string, number | undefined> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

const resolveNextCategoryIndex = (
  key: string,
  fromIndex: number,
  length: number
): number | null => {
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  const offset = CATEGORY_KEY_OFFSETS[key];
  if (offset === undefined) return null;
  return (fromIndex + offset + length) % length;
};

type CategoryChipsProps<T extends string> = {
  label: string;
  categories: AiFeedbackCategory<T>[];
  selectedCategory: T | null;
  onSelectCategory: (category: T) => void;
};

const CategoryChips = <T extends string>({
  label,
  categories,
  selectedCategory,
  onSelectCategory,
}: CategoryChipsProps<T>) => {
  const groupRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const selectedIndex = categories.findIndex(
    category => category.id === selectedCategory
  );
  const tabbableIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const handleKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    fromIndex: number
  ) => {
    const nextIndex = resolveNextCategoryIndex(
      event.key,
      fromIndex,
      categories.length
    );
    if (nextIndex === null) return;
    event.preventDefault();
    onSelectCategory(categories[nextIndex].id);
    groupRef.current
      ?.querySelectorAll<HTMLElement>('[role="radio"]')
      [nextIndex]?.focus();
  };

  return (
    <Box paddingTop="medium" paddingBottom="small">
      <Box id={labelId}>
        <Text variant="caption" color="textSubtle">
          {label}
        </Text>
      </Box>
      <FlexBox
        ref={groupRef}
        paddingTop="xsmall"
        paddingBottom="xsmall"
        gap="small"
        role="radiogroup"
        aria-labelledby={labelId}
        aria-required
      >
        {categories.map((category, index) => {
          const isSelected = selectedCategory === category.id;
          return (
            <CustomChip
              key={category.id}
              role="radio"
              aria-checked={isSelected}
              tabIndex={index === tabbableIndex ? 0 : -1}
              iconName={isSelected ? IconName.CHECK_MARK : undefined}
              onKeyDown={event => handleKeyDown(event, index)}
              onClick={() => onSelectCategory(category.id)}
              isActive={isSelected}
            >
              {category.label}
            </CustomChip>
          );
        })}
      </FlexBox>
    </Box>
  );
};

const ExitSignal = ({ onExit }: { onExit: VoidFunction }) => {
  useEffect(() => onExit, [onExit]);
  return null;
};

type AiFeedbackModalProps<T extends string> = {
  isOpen: boolean;
  onExited: VoidFunction;
  labels: AiFeedbackLabels;
  categories: AiFeedbackCategory<T>[];
  selectedCategory: T | null;
  onSelectCategory: (category: T) => void;
  inputValue: string;
  onChangeInput: (value: string) => void;
  onClose: VoidFunction;
  onSubmit: VoidFunction;
};

const AiFeedbackModal = <T extends string>({
  isOpen,
  onExited,
  labels,
  categories,
  selectedCategory,
  onSelectCategory,
  inputValue,
  onChangeInput,
  onClose,
  onSubmit,
}: AiFeedbackModalProps<T>) => (
  <AnimatedModalDialog
    isOpen={isOpen}
    onClickOutside={onClose}
    onEscPress={onClose}
  >
    <CustomModalContainer modalSize={ModalSize.S}>
      <ExitSignal onExit={onExited} />
      <ModalHeader>{labels.modalTitle}</ModalHeader>
      <ModalBody>
        <Box padding="medium">
          <Text variant="text2" color="textDefault">
            {labels.modalDescription}
          </Text>
          <CategoryChips
            label={labels.modalCategoriesLabel}
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={onSelectCategory}
          />
          <CustomTextArea
            rows={4}
            aria-label={labels.modalInputPlaceholder}
            placeholder={labels.modalInputPlaceholder}
            value={inputValue}
            onValueChange={onChangeInput}
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <SecondaryButton onClick={onClose}>
          {labels.modalCancelButton}
        </SecondaryButton>
        <PrimaryButton onClick={onSubmit} isDisabled={!selectedCategory}>
          {labels.modalSubmitButton}
        </PrimaryButton>
      </ModalFooter>
    </CustomModalContainer>
  </AnimatedModalDialog>
);

const ThankYouMessage = ({ message }: { message: string }) => (
  <Box role="status">
    <Text variant="caption" color="textModerate">
      {message}
    </Text>
  </Box>
);

type ThankYouMessageWithCTAProps = {
  ctaRef: Ref<HTMLButtonElement>;
  onClickCta: VoidFunction;
  message: string;
  ctaLabel: string;
};

const ThankYouMessageWithCTA = ({
  ctaRef,
  onClickCta,
  message,
  ctaLabel,
}: ThankYouMessageWithCTAProps) => (
  <FlexBox align="center" gap="small">
    <ThankYouMessage message={message} />
    <TellUsWhy ref={ctaRef} type="button" onClick={onClickCta}>
      {ctaLabel}
    </TellUsWhy>
  </FlexBox>
);

export type AiFeedbackProps<T extends string = AiFeedbackNegativeCategory> = {
  isVisible: boolean;
  // Update this token to reset the component
  resetToken: string | number;
  i18nLocale?: I18nLocale;
  onClickPositive: VoidFunction;
  onClickNegative: VoidFunction;
  onSubmitNegativeFeedback: (feedback: AiNegativeFeedback<T>) => void;
  onHideFeedback: VoidFunction;
  // Defaults to Inaccurate, Incomplete, Error and Other
  categories?: AiFeedbackCategory<T>[];
  labels?: AiFeedbackLabelOverrides;
};

const AiFeedbackContent = <T extends string>({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  onClickPositive,
  onClickNegative,
  onSubmitNegativeFeedback,
  onHideFeedback,
  categories,
  labels,
}: Omit<AiFeedbackProps<T>, 'isVisible' | 'resetToken'>) => {
  const [formState, setFormState] =
    useState<AiFeedbackFormState<T>>(INITIAL_FORM_STATE);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const modeRef = useRef<HTMLDivElement>(null);
  const resolvedLabels = resolveLabels(i18nLocale, labels);
  const resolvedCategories = (categories ??
    buildDefaultCategories(i18nLocale)) as AiFeedbackCategory<T>[];
  const { mode, modalIsOpen, selectedCategory, extraInformation } = formState;

  useEffect(() => {
    if (mode === 'default') return;
    const focusTarget = mode === 'askingWhy' ? ctaRef.current : modeRef.current;
    focusTarget?.focus();
  }, [mode]);

  const handleModalExited = useCallback(() => {
    if (ctaRef.current) return;
    modeRef.current?.focus();
  }, []);

  const handleClickPositive = () => {
    onClickPositive();
    setFormState(state => ({ ...state, mode: 'thankYou' }));
  };

  const handleClickNegative = () => {
    onClickNegative();
    setFormState(state => ({ ...state, mode: 'askingWhy' }));
  };

  const handleSelectCategory = (selectedCategory: T) =>
    setFormState(state => ({ ...state, selectedCategory }));

  const handleChangeInput = (extraInformation: string) =>
    setFormState(state => ({ ...state, extraInformation }));

  const handleSubmit = () => {
    if (!modalIsOpen || !selectedCategory) return;
    onSubmitNegativeFeedback({ category: selectedCategory, extraInformation });
    setFormState(state => ({ ...state, mode: 'thankYou', modalIsOpen: false }));
  };

  return (
    <>
      <FlexBox width="fit-content" borderWidth={1} borderRadius="medium">
        <FlexBox
          paddingLeft="medium"
          paddingRight="small"
          align="center"
          minHeight={BAR_HEIGHT}
        >
          <ModeContent key={mode} ref={modeRef} tabIndex={-1}>
            {mode === 'default' && (
              <>
                <Text variant="caption" color="textModerate">
                  {resolvedLabels.question}
                </Text>
                <FlexBox gap="small">
                  <BouncyIconButton
                    buttonSize={ButtonSize.SMALL}
                    iconName={IconName.THUMB_UP}
                    label={resolvedLabels.answerPositive}
                    onClick={handleClickPositive}
                  />
                  <BouncyIconButton
                    buttonSize={ButtonSize.SMALL}
                    iconName={IconName.THUMB_DOWN}
                    label={resolvedLabels.answerNegative}
                    onClick={handleClickNegative}
                  />
                </FlexBox>
              </>
            )}
            {mode === 'thankYou' && (
              <ThankYouMessage message={resolvedLabels.thankYou} />
            )}
            {mode === 'askingWhy' && (
              <ThankYouMessageWithCTA
                ctaRef={ctaRef}
                onClickCta={() =>
                  setFormState(state => ({ ...state, modalIsOpen: true }))
                }
                message={resolvedLabels.thankYou}
                ctaLabel={resolvedLabels.tellUsWhy}
              />
            )}
          </ModeContent>
        </FlexBox>
        <Divider />
        <FlexBox align="center" justify="center" flexGrow={1}>
          <IconButton
            buttonSize={ButtonSize.SMALL}
            iconName={IconName.CLOSE}
            label={resolvedLabels.hideFeedback}
            onClick={onHideFeedback}
          />
        </FlexBox>
      </FlexBox>
      <AiFeedbackModal
        isOpen={modalIsOpen}
        onExited={handleModalExited}
        labels={resolvedLabels}
        categories={resolvedCategories}
        selectedCategory={selectedCategory}
        onSelectCategory={handleSelectCategory}
        inputValue={extraInformation}
        onChangeInput={handleChangeInput}
        onClose={() =>
          setFormState(state => ({ ...state, modalIsOpen: false }))
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export const AiFeedback = <T extends string = AiFeedbackNegativeCategory>({
  isVisible,
  resetToken,
  ...props
}: AiFeedbackProps<T>): ReactNode => {
  if (!isVisible) return null;
  return <AiFeedbackContent key={resetToken} {...props} />;
};

const modeEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(3px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModeContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.medium};
  animation: ${modeEnter} ${transitionTokens.medium} ${easingTokens.cubicOut};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const BouncyIconButton = styled(IconButton)`
  && {
    transition:
      all ${transitionTokens.medium} ease-in-out,
      transform ${transitionTokens.small} ${easingTokens.cubicOut};
  }
  &:hover {
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const Divider = styled(Box)`
  height: ${BAR_HEIGHT};
  width: 1px;
  background: ${colors.borderSubtle};
`;

const TellUsWhy = styled(UnstyledButton)`
  ${caption};
  color: ${colors.textAction};
  text-decoration: underline;
`;

const CustomChip = styled(Chip)`
  &:hover {
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(1px);
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover,
    &:active {
      transform: none;
    }
  }
`;

const CustomTextArea = styled(TextArea)`
  height: unset;
`;

const CustomModalContainer = styled(ModalContainer)`
  width: 526px;
`;
